import { AddAlt } from '@carbon/react/icons';
import { AtomField } from 'atoms/form-atoms';
import {
  Button,
  Column,
  ComposedModal,
  FlexGrid as Grid,
  InlineNotification,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@carbon/react';
import { FormRow } from 'components/forms';
import { atom, useAtom, useAtomValue } from 'jotai';
import { useEffect, useMemo, useState } from 'react';
import styles from './assign-structure-grids.module.scss';
import classNames from 'classnames';
import { GeologicInterval } from './geologic-interval';
import {
  GeologicIntervalQueryResponse,
  StructureGridQueryResponse,
} from 'types/api-responses';
import { structureGridAssignmentForm } from './util';
import { GridSurfaceList } from './grid-surface-list';
import { useGeologicIntervals } from '../hooks';
import { useGridSurfaces } from './hooks';

export const AssignStructureGrids = ({
  basinField,
  collectionField,
}: {
  basinField: AtomField<string>;
  collectionField: AtomField<
    Map<GeologicIntervalQueryResponse, StructureGridQueryResponse>
  >;
}) => {
  const basin = useAtomValue(basinField);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedGeologicIntervalId, setSelectedGeologicIntervalId] = useState<
    number | null
  >(null);

  const geologicalIntervalsData = useGeologicIntervals({
    basinName: null,
    paginated: false,
  });

  const gridAssignmentForm = useMemo(
    () => atom(structureGridAssignmentForm()),
    []
  );
  const gridAssignmentFormValues = useAtomValue(gridAssignmentForm);

  const [draftCollection, setDraftCollection] = useAtom(
    gridAssignmentFormValues.draftCollection
  );

  const [proposedCollection, setProposedCollection] = useAtom(
    gridAssignmentFormValues.proposedCollection
  );

  const [collection, setCollection] = useAtom(collectionField);

  const geologicalIntervals = useMemo(
    () =>
      geologicalIntervalsData.data?.results
        .filter((result) => result.basinName === basin.value)
        .sort((a, b) => a.positionalIndex - b.positionalIndex) ?? [],
    [geologicalIntervalsData, basin.value]
  );

  const selectedGeologicInterval = useMemo(
    () =>
      geologicalIntervals.find(
        (interval) => interval.id === selectedGeologicIntervalId
      ),
    [geologicalIntervals, selectedGeologicIntervalId]
  );

  const draftedCollection = new Map([
    ...collection.value,
    ...draftCollection.value,
  ]);

  const currentCollection = new Map([
    ...collection.value,
    ...proposedCollection.value,
    ...draftCollection.value,
  ]);

  useEffect(() => {
    if (geologicalIntervals.length > 0 && isModalOpen) {
      setSelectedGeologicIntervalId(geologicalIntervals[0].id);
    }
  }, [geologicalIntervals, isModalOpen]);

  const gridSurfacesData = useGridSurfaces(
    selectedGeologicIntervalId,
    selectedGeologicInterval?.geologicInterval ?? '',
    basinField
  );

  const handleAssignment = () => {
    setDraftCollection((collection) => {
      const selectedGridSurface = proposedCollection.value.get(
        selectedGeologicInterval!
      );
      if (selectedGridSurface && selectedGeologicInterval) {
        return new Map([
          ...collection,
          [selectedGeologicInterval, selectedGridSurface],
        ]);
      }
      return new Map([...collection]);
    });

    setProposedCollection((collection) => {
      collection.delete(selectedGeologicInterval!);
      return collection;
    });
  };

  const commitAssignment = () => {
    setCollection((collection) => {
      const mergedCollection = new Map([
        ...collection,
        ...Array.from(draftCollection.value),
      ]);

      const result = new Map(
        [...mergedCollection.entries()].filter(([_k, v]) => v !== null)
      ) as Map<GeologicIntervalQueryResponse, StructureGridQueryResponse>;
      return result;
    });

    setProposedCollection(new Map());
    setDraftCollection(new Map());

    setIsModalOpen(false);
  };

  const handleGridSurfaceSelection = (
    gridSurface?: StructureGridQueryResponse
  ) => {
    if (gridSurface) {
      setProposedCollection((collection) => {
        collection.set(selectedGeologicInterval!, gridSurface);
        return collection;
      });
    }
  };

  return (
    <>
      <ComposedModal
        size="lg"
        open={isModalOpen}
        preventCloseOnClickOutside
        onClose={() => setIsModalOpen(false)}
        className={styles.modalContainer}
      >
        <ModalHeader title="Assign Structure Grids to Collection" />
        <ModalBody
          hasScrollingContent
          aria-label="Assign Structure Grids to Collection"
        >
          <Grid
            fullWidth
            className={classNames(styles.modalBody, 'bx--no-gutter')}
          >
            <Row className={styles.row}>
              <Column className={classNames(styles.leftPane)}>
                <div className={styles.geologicIntervalMetadataContainer}>
                  <span>
                    Basin : <span>{basin.value}</span>
                  </span>
                  <span>
                    Geologic Intervals:&nbsp;
                    <span>{geologicalIntervals.length}</span>
                  </span>
                </div>
                <div className={styles.pane}>
                  {geologicalIntervals.map((geologicInterval) => {
                    const assignedGridSurface =
                      draftedCollection.get(geologicInterval);
                    const gridSurfaceProps = assignedGridSurface
                      ? {
                          name: assignedGridSurface.gridSurface,
                          owner: assignedGridSurface.owner,
                          createdAt: assignedGridSurface.createdAt,
                          source: assignedGridSurface.source,
                        }
                      : undefined;
                    return (
                      <div
                        key={geologicInterval.id}
                        className={classNames({
                          [styles.disabled]: gridSurfacesData.loading,
                        })}
                      >
                        <GeologicInterval
                          name={geologicInterval.geologicInterval}
                          selected={
                            selectedGeologicIntervalId === geologicInterval.id
                          }
                          onClick={() => {
                            setSelectedGeologicIntervalId(geologicInterval.id);
                          }}
                          gridSurface={gridSurfaceProps}
                          onClose={() => {
                            setDraftCollection((collection) => {
                              collection.set(geologicInterval, null);
                              return collection;
                            });
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              </Column>
              <Column className={classNames(styles.pane, styles.rightPane)}>
                <Row>
                  <Column className={styles.assignContainer}>
                    <p className={styles.geologicIntervalTitle}>
                      Select Structure Grid for&nbsp;
                      <span>{selectedGeologicInterval?.geologicInterval}</span>
                    </p>
                    <Button
                      data-testid="draft-assign-button"
                      size="sm"
                      renderIcon={AddAlt}
                      disabled={
                        !proposedCollection.value.get(selectedGeologicInterval!)
                      }
                      onClick={handleAssignment}
                    >
                      Assign
                    </Button>
                  </Column>
                </Row>
                <Row>
                  <Column>
                    {!gridSurfacesData.error ? (
                      <GridSurfaceList
                        selectedGridSurfaceId={
                          currentCollection.get(selectedGeologicInterval!)?.id
                        }
                        onSelectGridSurface={handleGridSurfaceSelection}
                        gridSurfaces={gridSurfacesData.gridSurfaces ?? null}
                        isLoading={gridSurfacesData.loading ?? false}
                      />
                    ) : (
                      <InlineNotification
                        kind="error"
                        title={gridSurfacesData.error}
                      ></InlineNotification>
                    )}
                  </Column>
                </Row>
              </Column>
            </Row>
          </Grid>
        </ModalBody>
        <ModalFooter>
          <Button
            kind="tertiary"
            disabled={gridSurfacesData.loading}
            onClick={() => {
              setIsModalOpen(false);
              setProposedCollection(new Map());
              setDraftCollection(new Map());
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={commitAssignment}
            disabled={!draftCollection.isValid || gridSurfacesData.loading}
          >
            Confirm
          </Button>
        </ModalFooter>
      </ComposedModal>
      <FormRow>
        <Button
          disabled={!basin.value || geologicalIntervalsData.loading}
          renderIcon={AddAlt}
          onClick={() => setIsModalOpen(true)}
        >
          Assign
        </Button>
      </FormRow>
      <FormRow>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader>Geologic Interval</TableHeader>
              <TableHeader>Grid Surface Assigned</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.from(collection.value.entries())
              .sort((a, b) => a[0].positionalIndex - b[0].positionalIndex)
              .map(([key, value]) => (
                <TableRow key={key.id}>
                  <TableCell>{key.geologicInterval}</TableCell>
                  <TableCell>{value?.gridSurface}</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </FormRow>
    </>
  );
};
